import { useEffect, useRef, useState } from 'react';

import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import { useConnectBankAccount } from 'v3/hooks/useConnectBankAccount';
import { useBankAccountLink } from 'v3/hooks/useBankAccountLink';
import { PaymentClient } from 'v3/api/client/PaymentClient';
import { PaymentParams } from 'v3/api/client/paymentParams';
import { isPlaidBankLink } from 'v3/api/core/apiConfig';
import { useExperiment } from '@marvelapp/react-ab-test';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { Check } from '@styled-icons/remix-fill/Check';

import { SIGN_FORM_ID, renderCtaInSlot, useCtaSlot } from './cta-slot';
import IconItem from 'v3/components/IconItem';
import { Bank, Close, Lock } from '@styled-icons/remix-line';
import Checkbox from 'v3/components/Checkbox';
import Button from 'v3/components/Button';
import ModalNotification from 'v3/components/ModalNotification';
import PromotionalModal from 'v3/components/PromotionalModal';
import ErrorFocus from 'v3/components/ErrorFocus';

/* The CTA is white now, so the white logo would be invisible on it. */
import BlackPlaidIco from 'v3/assets/svg/black-plaid-icon.svg';


import theme from 'styles/theme';
import * as S from '../styles';


const validationSchema = Yup.object().shape({
  notice1: Yup.boolean()
    .required('This field is required')
    .oneOf([true], 'This field is required'),
});

type ACHFormProps = {
  validateOnSubmit: () => boolean;
  signContract: (paymentParams: PaymentParams) => void;
  isDrawingValid?: boolean;
};

type ResumeVerificationButtonProps = {
  cfUuid: string;
  signContract: ACHFormProps['signContract'];
  setIsLoading: (loading: boolean) => void;
  setIsProcessing: (processing: string) => void;
  onExit: () => void;
  disabled?: boolean;
};

// Shown only when a Same-Day micro-deposit verification is pending. Opens Plaid in update mode
// (resume=true) so the customer can finish verifying the bank they already started, instead of
// linking a new one (which is what the primary button does by default).
//
// Deliberately NOT gated on the provider flag. The backend only reports pending when a Plaid
// access token is present (`UserGroup.micro_deposit_verification_pending`), so this shows for
// exactly the members who started a Plaid verification and nobody else. Hiding it when the flag
// flips to Stripe would strand that cohort mid-verification with no way to finish.
const ResumeVerificationButton = ({
  cfUuid,
  signContract,
  setIsLoading,
  setIsProcessing,
  onExit,
  disabled = false,
}: ResumeVerificationButtonProps) => {
  const { open, ready } = useConnectBankAccount({
    cfUuid,
    signContract,
    setIsLoading,
    setIsProcessing,
    onExit,
    resume: true,
  });

  return (
    <Button
      variant="secondary"
      type="button"
      disabled={disabled || !ready}
      onClick={() => open()}
    >
      Pending verification
    </Button>
  );
};

export const ACHForm = ({
  isDrawingValid,
  validateOnSubmit,
  signContract,
}: ACHFormProps) => {
  const { leadData } = useSelector((state: RootState) => state.lead);
  const plaidTriggerFunc = useRef<Function>(() => {});
  const [openExitModal, setOpenExitModal] = useState(false);
  const { selectVariant } = useExperiment('Plaid On Exit');
  const showPromotionalModal = selectVariant({
    A: true,
    B: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState('');
  const [isMicroDepositPending, setIsMicroDepositPending] = useState(false);
  const ctaSlot = useCtaSlot();

  const cfUuid = leadData.contractFlowToken;

  useEffect(() => {
    let isActive = true;

    PaymentClient()
      .paymentInfo(cfUuid)
      .then(({ data }) => {
        if (isActive && data) {
          setIsMicroDepositPending(Boolean(data.bankAccountManuallyVerificationPending));
        }
      });

    return () => {
      isActive = false;
    };
  }, [cfUuid]);

  const initialValues = {
    notice1: false,
  };

  return (
    <Formik
      isInitialValid={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async () => {

        if(!validateOnSubmit()) {
          return;
        }

        setIsLoading(true);
        setIsProcessing('checking_bank_account');

        plaidTriggerFunc.current();
         
      }}
    >
      {(formik) => {
        const { errors, touched, isValid } = formik;

        const onExit = () => {
          setOpenExitModal(true);
          setIsLoading(false);
          setIsProcessing('');
        };

        const { open, linkError } = useBankAccountLink({
          cfUuid,
          clubId: Number(leadData.clubId),
          userUuid: leadData.userUuid,
          signContract,
          setIsLoading,
          setIsProcessing,
          onExit,
          billingDetails: {
            name: `${leadData.firstName ?? ''} ${leadData.lastName ?? ''}`.trim(),
            email: leadData.email,
          },
        });

        plaidTriggerFunc.current = open;

        const loading = isLoading;       

        const buttonStatus = () => {
          if (loading || isProcessing === 'checking_bank_account') {
            return (
              <>
                <span>Selecting bank account</span> <S.ProcessingIcon size={18} />
              </>
            );
          }

          if (loading || isProcessing === 'processing') {
            return (
              <>
                <span>Processing payment</span> <S.ProcessingIcon size={18} />
              </>
            );
          }

          if (isProcessing === 'complete') {
            return (
              <>
                <span>All Done!</span> <Check size={18} />
              </>
            );
          }

          if (linkError) {
            return 'Try again';
          }

          return 'Sign and Pay';
        };

        return (
          <>
            {showPromotionalModal ? (
              <PromotionalModal
                title={
                  <>
                    Don't leave yet, <br /> <span>Get $50</span> off
                  </>
                }
                content={
                  <S.PromotionalModalContent>
                    <p>
                      <strong>Sign up and pay now</strong> and get <br />
                      $50 off on your loan amount
                    </p>

                    <div>
                      Still have questions?{' '}
                      <a id="contact-us-now">talk to us now</a>
                    </div>
                  </S.PromotionalModalContent>
                }
                onCancel={() => setOpenExitModal(false)}
                open={openExitModal}
              />
            ) : (
              <ModalNotification
                title="Whoops..."
                content="Looks like we’re still missing some bank account information. No worries, hit the button to retry entering your info!"
                buttonText="try again"
                onCancel={() => setOpenExitModal(false)}
                onClick={() => open()}
                open={openExitModal}
              />
            )}

            <FormikForm id={SIGN_FORM_ID}>
              <S.EncryptContent>
                <IconItem
                  icon={<Lock size={12} />}
                  text={
                    <S.LockText>
                      This is a secure and verified way to approve your account.
                    </S.LockText>
                  }
                />
              </S.EncryptContent>

              <S.Checkboxes>
                <S.FieldWrapper>
                  <Checkbox
                    name="notice1"
                    required
                    feedbackMessage={touched.notice1 && errors.notice1}
                    isValid={!(touched.notice1 && errors.notice1)}
                  >
                    <>
                      I have read, understand and agree to{' '}
                      <strong>ACH Authorization</strong> and{' '}
                      <strong>Savings Club</strong>{' '}
                      <Button
                        size="small"
                        variant="anchor"
                        as="a"
                        href="#agreement"
                      >
                        Participation Agreement
                      </Button>
                      .
                    </>
                  </Checkbox>
                </S.FieldWrapper>
              </S.Checkboxes>

              <S.ButtonSection>
                <S.Buttons>
                  {linkError && (
                    <S.ErrorMessage>
                      <Close size={20} color={theme.colors.statusDanger30} />
                      <p>Error processing your account</p>
                    </S.ErrorMessage>
                  )}

                  {isMicroDepositPending && (
                    <ResumeVerificationButton
                      cfUuid={cfUuid}
                      signContract={signContract}
                      setIsLoading={setIsLoading}
                      setIsProcessing={setIsProcessing}
                      onExit={onExit}
                      disabled={!isValid}
                    />
                  )}

                  {/* CTA rendered into the slot outside the card. It stays in this
                      React tree, so isValid/isProcessing keep working; the `form`
                      attribute restores the submit link the portal breaks. */}
                  {renderCtaInSlot(
                    <Button
                      icon={
                        loading ||
                        isProcessing === 'processing' ||
                        isProcessing === 'checking_bank_account' ||
                        isProcessing === 'complete' ? undefined : isPlaidBankLink ? (
                          <img src={BlackPlaidIco} alt="Plaid logo" />
                        ) : (
                          <Bank size={18} />
                        )
                      }
                      status={
                        isProcessing === 'processing'
                          ? 'info'
                          : isProcessing === 'complete'
                          ? 'success'
                          : undefined
                      }
                      iconPosition="left"
                      variant="brandPrimary"
                      disabled={
                        !isValid ||
                        !isDrawingValid ||
                        loading ||
                        isProcessing === 'processing' ||
                        isProcessing === 'complete'
                      }
                      type="submit"
                      form={SIGN_FORM_ID}
                    >
                      {buttonStatus()}
                    </Button>,
                    ctaSlot
                  )}
                </S.Buttons>
              </S.ButtonSection>

              <ErrorFocus />
            </FormikForm>
          </>
        );
      }}
    </Formik>
  );
};
